// Chat.jsx — v4.0. Refonte iOS + réponses, réactions emoji, glisser-déposer,
// raccourcis clavier, séparateurs de date, gestion de groupe avancée,
// safety number. Cycle de vie des URLs blob strictement maîtrisé (invariant 8).
// v4.1 : messages vocaux, édition de message, QR (vérif. contact), blocage.
import React, { useEffect, useRef, useState } from 'react';
import { fingerprint, safetyNumber } from '../crypto.js';
import { Avatar, Icon, dateLabel, QrCode, QrScanner } from '../ui.jsx';
import { qrPayload, parseQrPayload } from '../qr.js';
import { chooseRecorderMime, extensionForMime } from '../media-utils.js';
import { collectMentions } from '../collaboration-utils.js';
import { EPHEMERAL_OPTIONS, mediaForConversation, normalizeEphemeralSeconds } from '../messaging-tools.js';

const ICONS = { document: '📄', archive: '🗜️' };
const STATUS_MARK = { sending: '⏳', scheduled: '🕒', sent: '✓', delivered: '✓✓', read: '✓✓', failed: '⚠' };
const QUICK_EMOJI = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

function human(size) {
  if (!size) return '';
  if (size < 1024) return `${size} o`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} Ko`;
  return `${(size / 1024 / 1024).toFixed(1)} Mo`;
}
// Fix (post-livraison) : le Blob doit porter un type MIME explicite, sinon
// Safari refuse de lire une vidéo depuis une URL blob: (WebKit est strict,
// contrairement à Chrome/Firefox qui devinent parfois le type). Le mime exact
// (magic bytes) n'est connu qu'à l'émission et n'est pas retransmis (pas de
// nouvelle métadonnée serveur) : on le redérive ici depuis l'extension du nom
// de fichier déchiffré localement + le msgType déjà disponible des deux côtés.
const MIME_BY_EXT = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp',
  mp4: 'video/mp4', m4v: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime',
  pdf: 'application/pdf', txt: 'text/plain',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  zip: 'application/zip', rar: 'application/x-rar', '7z': 'application/x-7z-compressed',
  mp3: 'audio/mpeg', wav: 'audio/wav', m4a: 'audio/mp4', ogg: 'audio/ogg', oga: 'audio/ogg',
};
function guessMime(fileName, msgType) {
  const ext = (fileName || '').split('.').pop()?.toLowerCase();
  return MIME_BY_EXT[ext]
    || (msgType === 'image' ? 'image/jpeg' : msgType === 'video' ? 'video/mp4'
      : msgType === 'audio' ? 'audio/webm' : 'application/octet-stream');
}
function blobOf(m) {
  const type = (typeof m.mimeType === 'string' && m.mimeType) || guessMime(m.fileName, m.msgType);
  if (m.data) return new Blob(Array.isArray(m.data) ? m.data : [m.data], { type });
  if (m.blob) return m.blob.type ? m.blob : new Blob([m.blob], { type });
  return null;
}
function BlobMedia({ msg }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const b = blobOf(msg);
    if (!b) return undefined;
    const u = URL.createObjectURL(b);
    setUrl(u);
    return () => URL.revokeObjectURL(u); // révocation au démontage (invariant 8)
  }, [msg.localId]);
  if (!url) return null;
  // playsInline : évite que Safari iOS force le plein écran / refuse la
  // lecture inline pour une source blob:.
  if (msg.msgType === 'video') return <video className="inline-img" src={url} controls playsInline preload="metadata" />;
  if (msg.msgType === 'audio') return <audio src={url} controls preload="metadata" className="inline-audio" />;
  return <img className="inline-img" src={url} alt={msg.fileName || 'image'} />;
}

export default function Chat({
  identity, peer, group, contact, messages = [], online, typing = {}, keyAlert, blocked = [],
  onAcceptNewKey, onTyping = () => {}, onUnsend, onBack, onSend, onDeleteMessage, onDeleteForEveryone = () => {}, onConsumeViewOnce = () => {},
  onReact, onEdit, onUpdateGroup, onToggleBlocked, onAddContactByQr, onResend = () => {},
  replyTo, onSetReply = () => {}, sendQueue, allContacts = [], allGroups = [],
  onSendPoll = () => {}, onVotePoll = () => {}, shoppingItems = [],
  onAddShoppingItem = () => {}, onToggleShoppingItem = () => {}, onClearCheckedShopping = () => {}, onShareShopping = () => {},
  onImportShoppingList = () => {},
  onStartAudioCall = async () => {}, onStartVideoCall = async () => {},
  audioCallAvailable = false, videoCallAvailable = false, audioCallDisabledReason = '', videoCallDisabledReason = '', focusMessageId = null,
  viewOnceMode = false, onSetViewOnceMode = () => {}, autoStartVoiceToken = 0, onAutoStartVoiceHandled = () => {},
}) {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [showInfo, setShowInfo] = useState(false);   // panneau info (fingerprint / groupe)
  const [emojiFor, setEmojiFor] = useState(null);     // localId ciblé par le popover emoji
  const [dragOver, setDragOver] = useState(false);
  const [groupEdit, setGroupEdit] = useState(null);   // { name, add } bottom sheet
  const [editingId, setEditingId] = useState(null);   // v4.1 : localId en cours d'édition
  const [editingText, setEditingText] = useState('');
  const [recording, setRecording] = useState(false);  // v4.1 : enregistrement vocal en cours
  const [recSeconds, setRecSeconds] = useState(0);
  const [important, setImportant] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [showPoll, setShowPoll] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [shoppingText, setShoppingText] = useState('');
  const [mentionQuery, setMentionQuery] = useState(null);
  const [shoppingShareOpen, setShoppingShareOpen] = useState(false);
  const [showMediaGallery, setShowMediaGallery] = useState(false);
  const [viewOncePreview, setViewOncePreview] = useState(null);
  const [viewOnceRemaining, setViewOnceRemaining] = useState(0);
  const [ephemeralSeconds, setEphemeralSeconds] = useState(0);
  const stoppingRef = useRef(false);
  const recBytesRef = useRef(0);
  const [showMyQr, setShowMyQr] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const bottomRef = useRef(null);
  const fileInput = useRef(null);
  const taRef = useRef(null);
  const lastTypingSent = useRef(0);
  const recorderRef = useRef(null);
  const recChunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const autoVoiceHandledRef = useRef(0);

  const isSelfNotes = !group && !!identity && peer === identity.pseudo;
  const contactVerified = !keyAlert && !!(contact?.publicKey && contact?.verifiedKey === contact.publicKey && Number(contact?.verifiedAt || 0) > 0);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  // v4.9.3 : après un appel sans réponse, le bouton « laisser un vocal »
  // ouvre la conversation puis amorce le MediaRecorder. Le clic reste côté
  // client ; le vocal suit ensuite exactement le pipeline E2E habituel.
  useEffect(() => {
    const token = Number(autoStartVoiceToken || 0);
    if (!token || autoVoiceHandledRef.current === token) return undefined;
    autoVoiceHandledRef.current = token;
    const timer = setTimeout(async () => {
      try {
        if (keyAlert) setError('Vérifiez d’abord la nouvelle clé du contact avant de laisser un vocal.');
        else if (!recording) await startRecording();
      } finally { onAutoStartVoiceHandled(); }
    }, 120);
    return () => clearTimeout(timer);
  }, [autoStartVoiceToken, peer]);
  useEffect(() => {
    if (focusMessageId === null || focusMessageId === undefined) return undefined;
    const timer = setTimeout(() => {
      const target = [...document.querySelectorAll('[data-message-id]')].find((el) => el.dataset.messageId === String(focusMessageId));
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('search-hit');
      setTimeout(() => target.classList.remove('search-hit'), 2200);
    }, 120);
    return () => clearTimeout(timer);
  }, [focusMessageId, messages]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  // Échap : ferme les surcouches / annule la citation.
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') { setEmojiFor(null); setGroupEdit(null); setShowMediaGallery(false); onSetReply(null); setEditingId(null); } };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);
  // Nettoyage du minuteur d'enregistrement au démontage.
  useEffect(() => () => {
    if (recTimerRef.current) clearInterval(recTimerRef.current);
    const mr = recorderRef.current;
    try { if (mr && mr.state !== 'inactive') mr.stop(); } catch (_) {}
    try { mr?.stream?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    recorderRef.current = null; recChunksRef.current = [];
  }, []);
  // Textarea auto-hauteur.
  useEffect(() => {
    const ta = taRef.current; if (!ta) return;
    ta.style.height = 'auto'; ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
  }, [text]);

  // v4.2.1 : garde absolue, placée APRÈS tous les hooks (règles de React) —
  // sans identité (état transitoire pendant déconnexion / suppression de
  // compte), on ne rend rien plutôt que de planter sur identity.pseudo.
  if (!identity) return null;

  const typers = group
    ? (group.members || []).filter((pp) => pp !== identity.pseudo && typing[pp])
    : (typing[peer] ? [peer] : []);
  const unreachable = group
    ? (group.members || []).filter((pp) => pp !== identity.pseudo && !allContacts.some((c) => c.pseudo === pp))
    : [];

  function pickFile(f) {
    setFile(f || null);
    setPreview(f && f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
  }
  function onInput(v, cursorPosition) {
    setText(v);
    if (group) {
      const cursor = Number.isInteger(cursorPosition) ? cursorPosition : v.length;
      const beforeCursor = v.slice(0, cursor);
      const match = beforeCursor.match(/(?:^|\s)@([\p{L}\p{N}_.-]*)$/u);
      setMentionQuery(match ? match[1].toLocaleLowerCase('fr') : null);
    }
    const now = Date.now();
    if (v && now - lastTypingSent.current > 3000) { lastTypingSent.current = now; onTyping(); }
  }
  function insertMention(pseudo) {
    const ta = taRef.current;
    const cursor = ta?.selectionStart ?? text.length;
    const before = text.slice(0, cursor).replace(/(?:^|\s)@[\p{L}\p{N}_.-]*$/u, (m) => `${m.startsWith(' ') ? ' ' : ''}@${pseudo} `);
    setText(before + text.slice(cursor)); setMentionQuery(null);
    requestAnimationFrame(() => ta?.focus());
  }
  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(e); } // Entrée = envoyer
  }
  async function submit(e) {
    e.preventDefault?.();
    if (!text.trim() && !file) return;
    setError('');
    try {
      setProgress(file ? 0.05 : null);
      const deliverAt = scheduleAt ? new Date(scheduleAt).getTime() : null;
      if (deliverAt && deliverAt < Date.now() + 60000) throw new Error('Choisissez une programmation au moins une minute dans le futur.');
      const mentions = group ? collectMentions(text, group.members, identity.pseudo) : [];
      await onSend(text.trim(), file, setProgress, { important, deliverAt, mentions, ephemeralSeconds: normalizeEphemeralSeconds(ephemeralSeconds), viewOnce: viewOnceMode });
      setText(''); pickFile(null); setImportant(false); setScheduleAt(''); setEphemeralSeconds(0);
      if (fileInput.current) fileInput.current.value = '';
    } catch (err) { setError(err.message); }
    finally { setTimeout(() => setProgress(null), 400); }
  }
  function download(msg) {
    if (msg?.viewOnce) return;
    const b = blobOf(msg); if (!b) return;
    const url = URL.createObjectURL(b);
    const a = document.createElement('a');
    a.href = url; a.download = msg.fileName || 'fichier'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }
  async function openViewOnce(msg) {
    if (!msg?.viewOnce || msg.from === identity.pseudo) return;
    const b = blobOf(msg); if (!b) { setError('Ce média à ouverture unique n’est plus disponible.'); return; }
    const url = URL.createObjectURL(b);
    // L'ouverture n'est autorisée qu'après suppression réussie de la copie
    // IndexedDB. Le Blob déjà créé ne vit ensuite qu'en mémoire jusqu'à fermeture.
    try {
      await onConsumeViewOnce(msg);
      const expiresAt = Date.now() + 90_000;
      setViewOnceRemaining(90);
      setViewOncePreview({ url, msgType: msg.msgType, fileName: msg.fileName || 'Média', expiresAt });
    } catch (_) {
      URL.revokeObjectURL(url);
      setError('Impossible de sécuriser l’ouverture unique sur cet appareil. Le média n’a pas été ouvert.');
    }
  }
  function closeViewOncePreview() {
    if (viewOncePreview?.url) URL.revokeObjectURL(viewOncePreview.url);
    setViewOncePreview(null);
    setViewOnceRemaining(0);
  }

  // v4.8 : l'ouverture unique vit au maximum 90 s en mémoire et se ferme
  // immédiatement si l'app passe en arrière-plan.
  useEffect(() => {
    if (!viewOncePreview?.expiresAt) return undefined;
    const update = () => {
      const left = Math.max(0, Math.ceil((viewOncePreview.expiresAt - Date.now()) / 1000));
      setViewOnceRemaining(left);
      if (left <= 0) closeViewOncePreview();
    };
    update();
    const timer = setInterval(update, 1000);
    const hide = () => { if (document.visibilityState === 'hidden') closeViewOncePreview(); };
    document.addEventListener('visibilitychange', hide);
    window.addEventListener('pagehide', closeViewOncePreview);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', hide); window.removeEventListener('pagehide', closeViewOncePreview); };
  }, [viewOncePreview?.expiresAt]);

  // Glisser-déposer un fichier dans la conversation.
  function onDrop(e) {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  }

  // v4.1 : messages vocaux — MediaRecorder natif (aucune lib tierce). Le
  // Blob produit est envoyé via le MÊME pipeline que n'importe quelle pièce
  // jointe (chiffrement, upload) ; `__forceType` fait sauter la détection par
  // magic bytes côté App.jsx (inutile et ambiguë pour un contenu que l'app a
  // elle-même généré, cf. commentaire dans sendMessage).
  async function startRecording() {
    setError('');
    // v4.2.1 : MediaRecorder n'existe que depuis iOS 14.3 — sans cette
    // vérification, l'erreur levée serait un ReferenceError trompeur
    // (« Accès au micro refusé ») alors que c'est le navigateur qui est trop ancien.
    if (typeof window.MediaRecorder === 'undefined') {
      setError('Messages vocaux non pris en charge par ce navigateur (iOS 14.3 minimum).');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // v4.2.1 (interop iOS) : Safari ne décode PAS WebM/Opus — un vocal
      // enregistré en audio/webm (défaut Chrome/Firefox) est muet sur iPhone.
      // On préfère audio/mp4 (AAC) quand le navigateur émetteur sait le
      // produire : lisible partout, iPhone compris. À défaut, on laisse le
      // navigateur choisir (comportement inchangé).
      const selectedMime = chooseRecorderMime(window.MediaRecorder);
      let mr;
      try {
        mr = selectedMime ? new MediaRecorder(stream, { mimeType: selectedMime }) : new MediaRecorder(stream);
      } catch (_) {
        // Certains Firefox annoncent un type puis refusent le constructeur :
        // dernier repli = choix natif du navigateur.
        mr = new MediaRecorder(stream);
      }
      console.info('[vocal] recorder', { mimeType: mr.mimeType || selectedMime || 'browser-default', userAgent: navigator.userAgent });
      recChunksRef.current = []; recBytesRef.current = 0; stoppingRef.current = false;
      mr.ondataavailable = (e) => {
        if (e.data?.size > 0) {
          recChunksRef.current.push(e.data); recBytesRef.current += e.data.size;
          if (recBytesRef.current > 15 * 1024 * 1024) stopRecording(true);
        }
      };
      mr.onerror = () => { setError('Le navigateur a interrompu l’enregistrement vocal.'); stopRecording(false); };
      mr.onstop = () => stream.getTracks().forEach((t) => t.stop());
      mr.start(1000); // fragments réguliers : évite un Blob géant et les freezes Safari
      recorderRef.current = mr;
      setRecording(true); setRecSeconds(0);
      recTimerRef.current = setInterval(() => setRecSeconds((s) => {
        if (s >= 180) { stopRecording(true); return s; } // 3 min max, sécurité Safari
        return s + 1;
      }), 1000);
    } catch (_) { setError('Accès au micro refusé ou indisponible.'); }
  }
  async function stopRecording(send) {
    clearInterval(recTimerRef.current);
    const mr = recorderRef.current;
    if (!mr || stoppingRef.current) return;
    stoppingRef.current = true;
    setRecording(false);
    const done = new Promise((resolve) => {
      const timer = setTimeout(resolve, 4000);
      mr.onstop = () => { clearTimeout(timer); resolve(); mr.stream?.getTracks().forEach((t) => t.stop()); };
    });
    try { if (mr.state === 'recording') { try { mr.requestData(); } catch (_) {} mr.stop(); } } catch (_) {}
    await done;
    try { mr.stream?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    recorderRef.current = null; stoppingRef.current = false;
    if (!send || recChunksRef.current.length === 0) { recChunksRef.current = []; recBytesRef.current = 0; return; }
    const mimeType = mr.mimeType || 'audio/webm';
    const ext = extensionForMime(mimeType);
    const blob = new Blob(recChunksRef.current, { type: mimeType });
    recChunksRef.current = []; recBytesRef.current = 0;
    const voiceFile = {
      name: `vocal-${Date.now()}.${ext}`,
      type: mimeType,
      __forceType: 'audio',
      __mimeType: mimeType,
      arrayBuffer: () => blob.arrayBuffer(),
    };
    setError('');
    try {
      setProgress(0.05);
      await onSend('', voiceFile, setProgress, { ephemeralSeconds: normalizeEphemeralSeconds(ephemeralSeconds) });
      setEphemeralSeconds(0);
    } catch (err) { setError(err.message); }
    finally { setTimeout(() => setProgress(null), 400); }
  }
  function cancelRecording() { stopRecording(false); }

  // v4.1 : édition d'un message déjà envoyé (texte uniquement).
  function beginEdit(m) { setEditingId(m.localId); setEditingText(m.text || ''); }
  async function submitEdit(m) {
    const t = editingText.trim();
    setEditingId(null);
    if (!t || t === m.text) return;
    try { await onEdit(m, t); } catch (err) { setError(err.message); }
  }

  // v4.1 : scanner le QR de l'interlocuteur courant pour vérifier/re-confirmer sa clé.
  async function handleScanResult(raw) {
    setShowScanner(false);
    const parsed = parseQrPayload(raw);
    if (!parsed) { setError('QR code non reconnu.'); return; }
    try { await onAddContactByQr(parsed.pseudo, parsed.publicKey); }
    catch (err) { setError(err.message); }
  }

  const title = isSelfNotes ? 'Notes personnelles' : (group ? group.name : contact?.pseudo || peer);
  const isBlocked = !group && !isSelfNotes && blocked.includes(peer);
  const mediaItems = mediaForConversation(messages);

  // Regroupement par jour pour les séparateurs de date.
  const rows = [];
  let lastDay = null;
  for (const m of (messages || [])) {
    const day = new Date(m.sentAt).toDateString();
    if (day !== lastDay) { rows.push({ sep: dateLabel(m.sentAt), key: 'sep-' + day }); lastDay = day; }
    rows.push({ m, key: m.localId });
  }

  return (
    <div className="screen"
         onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
         onDragLeave={() => setDragOver(false)}
         onDrop={onDrop}>
      {/* En-tête translucide */}
      <header className="bar">
        <button className="ghost" onClick={onBack}><Icon.back /></button>
        <Avatar name={title} group={!!group} size="sm" />
        <div className="grow" onClick={() => setShowInfo(!showInfo)} style={{ cursor: 'pointer' }}>
          <strong>{title}</strong>
          {!group && contactVerified && <span className="verified-badge" title="Clé vérifiée par QR code ou validation manuelle">🔐 Vérifié</span>}
          {!group && <span className={`dot ${online ? 'on' : 'off'}`} />}
          <div className="muted small">
            {typers.length
              ? `${typers.join(', ')} ${typers.length > 1 ? 'écrivent' : 'écrit'}…`
              : group
                ? `${(group.members || []).length} membres${unreachable.length ? ` · ${unreachable.length} injoignable(s)` : ''}`
                : (online ? 'en ligne' : 'hors ligne')}
          </div>
        </div>
        {!group && !isSelfNotes && <button type="button" className="ghost call-start-button"
          disabled={!audioCallAvailable}
          title={audioCallAvailable ? `Appeler ${title}` : (audioCallDisabledReason || 'Appel audio indisponible')}
          onClick={async () => { try { setError(''); await onStartAudioCall(); } catch (e) { setError(e.message || 'Appel impossible.'); } }}>
          <Icon.phone />
        </button>}
        {!group && !isSelfNotes && <button type="button" className="ghost call-start-button video-start-button"
          disabled={!videoCallAvailable}
          title={videoCallAvailable ? `Appel vidéo avec ${title}` : (videoCallDisabledReason || 'Appel vidéo indisponible')}
          onClick={async () => { try { setError(''); await onStartVideoCall(); } catch (e) { setError(e.message || 'Appel vidéo impossible.'); } }}>
          <Icon.video />
        </button>}
        <button type="button" className="ghost media-gallery-button" disabled={!mediaItems.length} title="Médias et fichiers locaux" onClick={() => setShowMediaGallery(true)}>▦</button>
        {group && <button className="ghost" onClick={() => setGroupEdit({ name: group.name, add: '' })}><Icon.group /></button>}
      </header>

      {showInfo && !group && !isSelfNotes && contact && (
        <div className="fp-panel">
          {contactVerified && <div className="verified-panel">🔐 Contact vérifié localement{contact.verifiedAt ? ` · ${new Date(contact.verifiedAt).toLocaleDateString('fr-FR')}` : ''}</div>}
          <div>Empreinte de {contact.pseudo} (à comparer par un autre canal) :</div>
          <div className="fingerprint">{fingerprint(contact.publicKey)}</div>
          <div className="small muted" style={{ marginTop: 6 }}>Code de vérification (plus simple à lire à voix haute) :</div>
          <div className="fingerprint">{safetyNumber(contact.publicKey)}</div>
          <div className="row" style={{ marginTop: 8, gap: 6, flexWrap: 'wrap' }}>
            <button type="button" className="ghost" onClick={() => setShowMyQr(true)}><Icon.qrcode /> Mon QR</button>
            <button type="button" className="ghost" onClick={() => setShowScanner(true)}>Scanner le sien</button>
            <button type="button" className="ghost" onClick={() => onToggleBlocked(peer)}>
              <Icon.block /> {isBlocked ? 'Débloquer' : 'Bloquer'}
            </button>
          </div>
        </div>
      )}
      {showInfo && group && (
        <div className="fp-panel">
          Membres : {(group.members || []).join(', ')}
          {unreachable.length > 0 && <div className="small" style={{ color: 'var(--danger)' }}>Injoignable(s) : {unreachable.join(', ')}</div>}
        </div>
      )}

      {keyAlert && (
        <div className="key-alert">
          <strong>⚠️ La clé de {keyAlert.pseudo} a changé.</strong>
          <p className="small">Compte recréé, ou tentative d'usurpation. Vérifiez le nouveau code par un autre canal avant de continuer. Envoi bloqué.</p>
          <div className="small">Avant : <span className="fingerprint inline">{keyAlert.oldFp}</span></div>
          <div className="small">Après : <span className="fingerprint inline">{keyAlert.newFp}</span></div>
          <button onClick={onAcceptNewKey}>J'ai vérifié — accepter</button>
        </div>
      )}

      {/* Messages */}
      <div className="messages">
        {rows.map((r) => r.sep
          ? <div className="date-sep" key={r.key}><span>{r.sep}</span></div>
          : <MessageRow key={r.key} m={r.m} mine={r.m.from === identity.pseudo} group={group}
              onDelete={() => onDeleteMessage(r.m.localId)}
              onDeleteForEveryone={() => onDeleteForEveryone(r.m)}
              onUnsend={() => onUnsend(r.m)}
              onReply={() => onSetReply(r.m)}
              onEmoji={() => setEmojiFor(emojiFor === r.m.localId ? null : r.m.localId)}
              emojiOpen={emojiFor === r.m.localId}
              onReact={(e) => { onReact(r.m, e); setEmojiFor(null); }}
              onDownload={() => download(r.m)}
              onOpenViewOnce={() => openViewOnce(r.m)}
              onResend={() => onResend(r.m)}
              editing={editingId === r.m.localId}
              editingText={editingText}
              onSetEditingText={setEditingText}
              onBeginEdit={() => beginEdit(r.m)}
              onSubmitEdit={() => submitEdit(r.m)}
              onCancelEdit={() => setEditingId(null)} onVotePoll={(index) => onVotePoll(r.m, index)}
              onImportShoppingList={(onlyNew) => onImportShoppingList(r.m.shoppingList?.items, onlyNew)} />
        )}
        <div ref={bottomRef} />
      </div>

      {/* File d'envoi globale */}
      {sendQueue > 1 && <div className="queue-pill">Envoi de {sendQueue} messages…</div>}

      {/* Composition */}
      {progress !== null && <div className="progress"><div style={{ width: `${progress * 100}%` }} /></div>}
      {error && <p className="error pad">{error}</p>}
      {replyTo && (
        <div className="reply-bar">
          <div className="grow small"><strong>Réponse à </strong>{(replyTo.text || replyTo.fileName || 'pièce jointe').slice(0, 60)}</div>
          <button className="ghost" onClick={() => onSetReply(null)}>✕</button>
        </div>
      )}
      {file && (
        <div className="attach-preview">
          {preview ? <img src={preview} alt="" /> : <span>{ICONS.document} {file.name} ({human(file.size)})</span>}
          <button className="ghost" onClick={() => { pickFile(null); fileInput.current.value = ''; }}>✕ retirer</button>
        </div>
      )}
      {!recording && (
        <div className="composer-options">
          <button type="button" className={important ? 'important-toggle active' : 'ghost'} onClick={() => setImportant((v) => !v)} title="Message important">❗</button>
          <label className="schedule-control" title="Programmer l’envoi">🕒 <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} /></label>
          <button type="button" className="ghost" onClick={() => setShowPoll(true)} title="Créer un sondage">📊</button>
          <label className="ephemeral-control" title="Suppression locale automatique après la durée choisie">⌛ <select value={ephemeralSeconds} onChange={(e) => setEphemeralSeconds(normalizeEphemeralSeconds(e.target.value))}>{EPHEMERAL_OPTIONS.map((opt) => <option key={opt.seconds} value={opt.seconds}>{opt.label}</option>)}</select></label>
          {!isSelfNotes && <label className={`snap-toggle ${viewOnceMode ? 'active' : ''}`} title="Les prochaines photos/vidéos seront visibles une seule fois jusqu’à désactivation"><input type="checkbox" checked={viewOnceMode} onChange={(e) => onSetViewOnceMode(e.target.checked)} /> 👁️ Média 1 vue</label>}
        </div>
      )}
      {isSelfNotes && (
        <div className="shopping-panel">
          <strong>🛒 Liste de courses</strong>
          <div className="row"><input value={shoppingText} onChange={(e) => setShoppingText(e.target.value)} placeholder="Ajouter un article" onKeyDown={(e) => { if (e.key === 'Enter' && shoppingText.trim()) { e.preventDefault(); onAddShoppingItem(shoppingText.trim()); setShoppingText(''); } }} /><button type="button" onClick={() => { if (shoppingText.trim()) { onAddShoppingItem(shoppingText.trim()); setShoppingText(''); } }}>Ajouter</button></div>
          {shoppingItems.map((item) => <label key={item.id} className="shopping-item"><input type="checkbox" checked={!!item.checked} onChange={() => onToggleShoppingItem(item.id)} /> <span>{item.text}</span></label>)}
          <div className="row"><button type="button" className="ghost" onClick={onClearCheckedShopping}>Effacer cochés</button><button type="button" className="ghost" onClick={() => setShoppingShareOpen(true)}>Envoyer à un contact ou groupe</button></div>
        </div>
      )}
      {group && mentionQuery !== null && (() => {
        const members = (group.members || [])
          .filter((pp) => pp !== identity.pseudo)
          .map(String)
          .filter(Boolean)
          .sort((a, b) => {
            const al = a.toLocaleLowerCase('fr'), bl = b.toLocaleLowerCase('fr');
            const as = al.startsWith(mentionQuery), bs = bl.startsWith(mentionQuery);
            return as === bs ? al.localeCompare(bl, 'fr') : as ? -1 : 1;
          })
          .filter((pp) => pp.toLocaleLowerCase('fr').includes(mentionQuery))
          .slice(0, 8);
        return <div className="mention-suggestions" role="listbox" aria-label="Membres du groupe">
          {members.length ? members.map((pp) => (
            <button type="button" role="option" key={pp} onClick={() => insertMention(pp)}>@{pp}</button>
          )) : <span className="small muted">Aucun pseudo correspondant</span>}
        </div>;
      })()}
      <form className="composer" onSubmit={submit}>
        <input type="file" ref={fileInput} hidden
          accept=".jpg,.jpeg,.png,.webp,.gif,.mp4,.webm,.mov,.pdf,.docx,.xlsx,.txt,.zip,.rar,.7z,.mp3,.wav,.m4a,.ogg,.oga"
          onChange={(e) => pickFile(e.target.files[0])} />
        <button type="button" className="ghost big"
          title={group ? 'Joindre (20 Mo — vidéos hors groupe)' : 'Joindre (images/docs 20 Mo, vidéos 100 Mo)'}
          onClick={() => fileInput.current.click()}><Icon.clip /></button>
        {recording ? (
          <div className="grow rec-indicator">
            🔴 {Math.floor(recSeconds / 60)}:{String(recSeconds % 60).padStart(2, '0')}
            <button type="button" className="ghost" onClick={cancelRecording}>Annuler</button>
          </div>
        ) : (
          <>
            {text.length > 500 && <span className="char-count">{text.length}</span>}
            <textarea ref={taRef} className="grow" rows={1} value={text}
              onChange={(e) => onInput(e.target.value, e.target.selectionStart)} onKeyDown={onKeyDown}
              placeholder="Message…" disabled={!!keyAlert} />
          </>
        )}
        {recording ? (
          <button type="button" className="big" title="Envoyer le message vocal" onClick={() => stopRecording(true)}><Icon.stop /></button>
        ) : !text.trim() && !file ? (
          <button type="button" className="ghost big" title="Message vocal" disabled={!!keyAlert} onClick={startRecording}><Icon.mic /></button>
        ) : (
          <button className="big" disabled={(progress !== null && progress < 1) || !!keyAlert}><Icon.send /></button>
        )}
      </form>

      {dragOver && <div className="sheet-backdrop" style={{ pointerEvents: 'none' }}><div className="sheet">Déposez le fichier ici</div></div>}

      {/* Bottom sheet : mon QR code */}
      {showMyQr && (
        <div className="sheet-backdrop" onClick={() => setShowMyQr(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
            <h1>Mon QR code</h1>
            <QrCode text={qrPayload(identity.pseudo, identity.publicKey)} />
            <p className="small muted">{identity.pseudo}</p>
            <button className="ghost" onClick={() => setShowMyQr(false)}>Fermer</button>
          </div>
        </div>
      )}
      {showScanner && <QrScanner onResult={handleScanResult} onClose={() => setShowScanner(false)} />}

      {showPoll && (
        <div className="sheet-backdrop" onClick={() => setShowPoll(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <h1>Nouveau sondage</h1>
            <input value={pollQuestion} onChange={(e) => setPollQuestion(e.target.value)} placeholder="Question" />
            {pollOptions.map((o, i) => <input key={i} value={o} onChange={(e) => setPollOptions((prev) => prev.map((x, j) => j === i ? e.target.value : x))} placeholder={`Option ${i + 1}`} />)}
            {pollOptions.length < 6 && <button type="button" className="ghost" onClick={() => setPollOptions((v) => [...v, ''])}>+ Option</button>}
            <button type="button" onClick={async () => { const opts = pollOptions.map((x) => x.trim()).filter(Boolean); if (pollQuestion.trim() && opts.length >= 2) { await onSendPoll(pollQuestion.trim(), opts); setShowPoll(false); setPollQuestion(''); setPollOptions(['', '']); } }}>Envoyer le sondage</button>
            <button type="button" className="ghost" onClick={() => setShowPoll(false)}>Annuler</button>
          </div>
        </div>
      )}

      {showMediaGallery && (
        <div className="sheet-backdrop" onClick={() => setShowMediaGallery(false)}>
          <div className="sheet media-gallery-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}><h1>Médias et fichiers</h1><button type="button" className="ghost" onClick={() => setShowMediaGallery(false)}>✕</button></div>
            {!mediaItems.length ? <p className="muted">Aucun média local dans cette conversation.</p> : <div className="media-gallery-grid">
              {mediaItems.map((item) => <div className="media-gallery-item" key={item.localId}>
                {(item.msgType === 'image' || item.msgType === 'video') ? <BlobMedia msg={item} /> : <div className="media-gallery-file-icon">{item.msgType === 'audio' ? '🎤' : ICONS[item.msgType] || '📎'}</div>}
                <div className="small"><strong>{item.fileName || (item.msgType === 'audio' ? 'Message vocal' : item.msgType)}</strong></div>
                <div className="small muted">{new Date(item.sentAt).toLocaleString('fr-FR')}</div>
                <button type="button" className="ghost small" onClick={() => download(item)}>⬇ Enregistrer</button>
              </div>)}
            </div>}
            <p className="small muted">Galerie construite uniquement depuis l’IndexedDB de cet appareil. Aucun index média n’est créé côté serveur.</p>
          </div>
        </div>
      )}

      {viewOncePreview && (
        <div className="sheet-backdrop view-once-backdrop" onClick={closeViewOncePreview}>
          <div className="view-once-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="view-once-title">👁️ Ouverture unique</div>
            {viewOncePreview.msgType === 'video'
              ? <video src={viewOncePreview.url} controls autoPlay playsInline />
              : <img src={viewOncePreview.url} alt={viewOncePreview.fileName} />}
            <p className="small muted">Ce média a déjà été supprimé du stockage local. Fermeture définitive dans {viewOnceRemaining || 0} s maximum.</p>
            <button type="button" onClick={closeViewOncePreview}>Fermer définitivement</button>
          </div>
        </div>
      )}

      {shoppingShareOpen && (
        <div className="sheet-backdrop" onClick={() => setShoppingShareOpen(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <h1>Partager la liste</h1>
            <p className="small muted">Chaque destinataire pourra l’importer dans sa propre liste.</p>
            <div className="share-destination-list">
              {allContacts.filter((c) => c.pseudo !== identity.pseudo).map((c) => <button type="button" className="ghost" key={c.pseudo} onClick={async () => { await onShareShopping(shoppingItems, c.pseudo); setShoppingShareOpen(false); }}>👤 {c.pseudo}</button>)}
              {allGroups.map((g) => <button type="button" className="ghost" key={g.groupId} onClick={async () => { await onShareShopping(shoppingItems, g.groupId); setShoppingShareOpen(false); }}>👥 {g.name}</button>)}
            </div>
            <button type="button" className="ghost" onClick={() => setShoppingShareOpen(false)}>Annuler</button>
          </div>
        </div>
      )}

      {/* Bottom sheet : gestion de groupe */}
      {groupEdit && (
        <div className="sheet-backdrop" onClick={() => setGroupEdit(null)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <h1>Gérer le groupe</h1>
            <label className="small muted">Nom du groupe</label>
            <input value={groupEdit.name} onChange={(e) => setGroupEdit({ ...groupEdit, name: e.target.value })} />
            <label className="small muted">Membres actuels</label>
            <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
              {(group?.members || []).filter((pp) => pp !== identity.pseudo).map((pp) => (
                <span key={pp} className="reaction">
                  {pp}
                  <button type="button" className="ghost" style={{ padding: '0 4px' }}
                    onClick={() => onUpdateGroup(group.groupId, { members: (group.members || []).filter((x) => x !== pp) })}>✕</button>
                </span>
              ))}
            </div>
            <label className="small muted">Ajouter des membres (pseudos séparés par des virgules)</label>
            <input value={groupEdit.add} onChange={(e) => setGroupEdit({ ...groupEdit, add: e.target.value })} placeholder="alice, bob" />
            <button onClick={() => {
              const added = groupEdit.add.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean)
                .filter((pp) => !(group.members || []).includes(pp));
              const members = [...new Set([...(group.members || []), ...added])];
              onUpdateGroup(group.groupId, { name: groupEdit.name.trim() || group.name, members, added });
              setGroupEdit(null);
            }}>Enregistrer</button>
            <button className="ghost" onClick={() => setGroupEdit(null)}>Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

function renderMessageText(text, mentions = []) {
  const value = String(text || '');
  const mentionSet = new Set((mentions || []).map(String));
  if (!mentionSet.size) return value;
  return value.split(/(@[\p{L}\p{N}_.-]+)/gu).map((part, index) =>
    part.startsWith('@') && mentionSet.has(part.slice(1))
      ? <mark className="mention" key={`${part}-${index}`}>{part}</mark>
      : <React.Fragment key={index}>{part}</React.Fragment>);
}

function MessageRow({
  m, mine, group, onDelete, onDeleteForEveryone, onUnsend, onReply, onEmoji, emojiOpen, onReact, onDownload, onOpenViewOnce, onResend,
  editing, editingText, onSetEditingText, onBeginEdit, onSubmitEdit, onCancelEdit, onVotePoll = () => {},
  onImportShoppingList = () => {},
}) {
  // v4.3 (tactile) : les actions (réagir, répondre, modifier…) n'étaient
  // visibles qu'au survol souris (:hover) — inaccessibles sur iOS/Android.
  // Un appui sur la bulle les affiche/masque désormais (le survol continue de
  // fonctionner sur ordinateur, cf. styles.css).
  const [actionsOpen, setActionsOpen] = useState(false);
  const hasFile = !!(m.data || m.blob);
  // v4.3 : renvoi proposé si échec, ou si bloqué en « envoi » depuis > 30 s
  // (confirmation serveur jamais reçue — connexion perdue au moment d'envoyer).
  const resendable = mine && (m.status === 'failed'
    || (m.status === 'sending' && Date.now() - m.sentAt > 30000));
  const cancellable = mine && m.serverIds?.length && (m.status === 'sent' || m.status === 'sending' || m.status === 'scheduled') && !m.deliveredCount;
  const editable = mine && m.msgType === 'text' && m.text && (m.status === 'sent' || m.status === 'delivered' || m.status === 'read');
  return (
    <div className={`bubble-row ${mine ? 'mine' : 'theirs'}`} data-message-id={String(m.localId)}>
      {group && !mine && <div className="sender">{m.from}</div>}
      <div className={`bubble ${mine ? 'mine' : 'theirs'} ${m.status === 'failed' ? 'failed' : ''} ${m.important ? 'important-message' : ''} ${actionsOpen ? 'actions-open' : ''}`}
           onClick={() => { if (emojiOpen) onEmoji(); else setActionsOpen((v) => !v); }}>
        <span className="msg-actions" onClick={(e) => e.stopPropagation()}>
          <button title="Répondre" onClick={onReply}>↩</button>
          {editable && <button title="Modifier" onClick={onBeginEdit}><Icon.edit /></button>}
          {cancellable && <button title="Annuler l'envoi" onClick={onUnsend}>↺</button>}
          <button title="Supprimer localement" onClick={onDelete}>✕</button>
          {mine && m.status !== 'scheduled' && (m.mid || m.serverId) && <button title="Supprimer pour tout le monde" onClick={onDeleteForEveryone}>🗑️ tous</button>}
        </span>
        <button type="button" className="reaction-trigger" title="Réagir" aria-label="Réagir au message" onPointerDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActionsOpen(true); onEmoji(); }}>☺</button>
        {m.important && <div className="important-label">❗ Important</div>}
        {m.status === 'scheduled' && <div className="small muted">🕒 Programmé pour {new Date(m.deliverAt).toLocaleString()}</div>}
        {m.replyPreview && <div className="reply-quote">{m.replyPreview}</div>}
        {(m.msgType === 'image' || m.msgType === 'video') && m.viewOnce && (
          <div className="view-once-message">
            <div className="view-once-icon">👁️</div>
            <strong>Média à ouverture unique</strong>
            {mine
              ? <span className="small muted">Envoyé · aucune copie média conservée dans votre galerie locale.</span>
              : hasFile
                ? <button type="button" onClick={onOpenViewOnce}>Ouvrir une fois</button>
                : <span className="small muted">Déjà ouvert ou indisponible.</span>}
          </div>
        )}
        {(m.msgType === 'image' || m.msgType === 'video' || m.msgType === 'audio') && hasFile && !m.viewOnce && (
          <div className="media-wrap">
            <BlobMedia msg={m} />
            <button className="save-media-btn" title="Enregistrer" onClick={onDownload}>⬇ Enregistrer</button>
          </div>
        )}
        {(m.msgType === 'document' || m.msgType === 'archive') && hasFile && (
          <button className="file-chip" onClick={onDownload}>
            {ICONS[m.msgType]} {m.fileName} <span className="muted small">({human(m.fileSize)})</span> ⬇
          </button>
        )}
        {editing ? (
          <div className="edit-row">
            <textarea autoFocus rows={1} value={editingText}
              onChange={(e) => onSetEditingText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSubmitEdit(); }
                if (e.key === 'Escape') onCancelEdit();
              }} />
            <div className="row" style={{ gap: 6, marginTop: 4 }}>
              <button type="button" onClick={onSubmitEdit}>Enregistrer</button>
              <button type="button" className="ghost" onClick={onCancelEdit}>Annuler</button>
            </div>
          </div>
        ) : (
          <>
            {m.text && <div className="message-text">{renderMessageText(m.text, m.mentions)}{m.edited && <span className="small muted"> (modifié)</span>}</div>}
            {m.shoppingList && <div className="shopping-message-card"><strong>🛒 {m.shoppingList.title || 'Liste de courses'}</strong><ul>{(m.shoppingList.items || []).map((item, i) => <li key={`${item}-${i}`}>{item}</li>)}</ul><div className="row shopping-import-actions"><button type="button" onClick={(e) => { e.stopPropagation(); onImportShoppingList(true); }}>Importer les nouveaux</button><button type="button" className="ghost" onClick={(e) => { e.stopPropagation(); onImportShoppingList(false); }}>Tout importer</button></div></div>}
            {m.poll && <div className="poll-card"><strong>{m.poll.question}</strong>{(m.poll.options || []).map((opt, i) => { const votes = (m.poll.votes?.[i] || []); return <button type="button" key={i} className="poll-option" onClick={(e) => { e.stopPropagation(); onVotePoll(i); }}>{opt} <span>{votes.length}</span></button>; })}</div>}
          </>
        )}
        {m.expiresAt > Date.now() && <div className="ephemeral-stamp">⌛ Disparaît {new Date(m.expiresAt).toLocaleString('fr-FR')}</div>}
        {emojiOpen && (
          <div className="emoji-pop emoji-pop-top-left">
            {QUICK_EMOJI.map((e) => <button type="button" key={e} onPointerDown={(ev) => ev.stopPropagation()} onTouchStart={(ev) => ev.stopPropagation()} onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); onReact(e); }}>{e}</button>)}
          </div>
        )}
      </div>
      {m.reactions && Object.keys(m.reactions).length > 0 && (
        <div className="reactions">
          {Object.entries(m.reactions).map(([e, who]) => (
            <span key={e} className="reaction">{e} {who.length > 1 ? who.length : ''}</span>
          ))}
        </div>
      )}
      {resendable && (
        <button type="button" className="resend-chip"
          title="Renvoie le message (nouvel envoi complet — si l'original était en réalité parti, le destinataire le recevra deux fois)"
          onClick={(e) => { e.stopPropagation(); onResend(); }}>
          ↻ Renvoyer
        </button>
      )}
      <div className="stamp">
        {new Date(m.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        {mine && m.status && (
          <span title={m.status === 'read' ? 'Lu' : m.status === 'delivered' ? 'Délivré'
            : m.status === 'sent' ? 'Envoyé' : m.status === 'failed' ? 'Échec' : 'En cours'}>
            {' '}{STATUS_MARK[m.status]}{m.status === 'read' ? ' lu' : ''}
          </span>
        )}
      </div>
    </div>
  );
}
